import { Box, Text, useToast, VStack } from "@chakra-ui/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { humanize } from "../../utils";
import {
  approveMessageCampaign,
  getBroadcastMessages,
  pauseMessageCampaign,
  rejectMessageCampaign,
  resumeMessageCampaign,
} from "../../utils/api/broadcast.api";
import { useUserInfo } from "../../utils/hooks/useUserInfo";
import { Dealership_Roles } from "../../utils/roles";
import { Drawer, Dropdown, PaginatedTable } from "../common";
import DatePicker from "../common/DateRangePicker";
import Loader from "../common/Spinner";
import CreateBulkMessageForm from "./CreateBulkMessage";
import PauseModal from "./PauseModal";

const throttlingOptions = {
  two_hundred: "200 per hour",
  four_hundred: "400 per hour",
  six_hundred: "600 per hour",
  eight_hundred: "800 per hour",
  all_time: "Max",
};

const tableHeader = [
  { id: 1, value: "ID", label: "uuid" },
  { id: 2, value: "Title", label: "title" },
  { id: 3, value: "Requested Messages", label: "total_sms" },
  { id: 4, value: "Engagement Rate", label: "engagement_rate" },
  { id: 5, value: "Completed Percentage", label: "completed_percentage" },
  { id: 6, value: "Message Received", label: "message_recieved" },
  { id: 7, value: "Failed", label: "failed_sms" },
  { id: 8, value: "Unsubscribed", label: "total_optout" },
  { id: 9, value: "Message Content", label: "content" },
  { id: 10, value: "Scheduled At", label: "broadcast_time" },
  { id: 11, value: "Status", label: "message_status" },
  { id: 12, value: "Throttling Rate", label: "throttling_rate" },
  { id: 13, value: "User", label: "initiator_name" },
  { id: 14, value: "Actions", label: "actions" },
];

const editOptions = [
  { label: "Edit", value: "edit" },
  { label: "Approve", value: "approve" },
  { label: "Reject", value: "reject" },
];

type BroadcastMessageListingProps = {
  type: string;
  shouldRefetch?: boolean;
  setShouldRefetch?: React.Dispatch<React.SetStateAction<boolean>>;
  dateRange?: any;
  parentRefetch?: any;
  setDateRange: any;
};

function BroadcastMessageListing({
  type,
  shouldRefetch,
  setShouldRefetch,
  dateRange,
  parentRefetch,
  setDateRange,
}: BroadcastMessageListingProps) {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [isPauseModalOpen, setIsPauseModalOpen] = useState(false);
  const [isOpenMsg, setIsOpenMsg] = useState(false);

  const onOpenMsg = () => setIsOpenMsg(true);
  const onCloseMsg = () => setIsOpenMsg(false);

  const navigate = useNavigate();

  const user = useUserInfo("user");
  const isSalesPerson = Dealership_Roles[2] === user?.role;

  const toast = useToast();

  const { data, isLoading, refetch } = useQuery({
    queryKey: [`messages-${type}`, page, perPage, dateRange],
    queryFn: () =>
      getBroadcastMessages({
        status: type,
        page,
        perPage,
        dealership_id: user?.dealership?.id,
        dateRange,
      }),
    refetchOnWindowFocus: false,
    retry: false,
  });

  const mutation = useMutation<any, Error, any, unknown>({
    mutationFn: async (payload) => {
      try {
        const response = await pauseMessageCampaign({
          dealership_id: user?.dealership?.id,
          id: selectedCampaign,
          payload,
        });
        return { data: response.data };
      } catch (error) {
        throw error;
      }
    },
    onSuccess: () => {
      toast({
        description: "Updated successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
      setIsPauseModalOpen(false);
      refetch();
      parentRefetch && parentRefetch();
    },
    onError: (error: any) => {
      toast({
        description: `Error Updating: ${
          error?.response?.data?.errors?.toString() ?? "Something went wrong"
        }`,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    },
  });

  const statusMutation = useMutation<any, Error, any, unknown>({
    mutationFn: async (payload) => {
      try {
        const response = await (payload.status === "approve"
          ? approveMessageCampaign
          : rejectMessageCampaign)({
          dealership_id: user?.dealership?.id,
          id: payload.id,
          status: payload.status,
          campaignId: payload.campaignId,
          payload,
        });
        return { data: response.data };
      } catch (error) {
        throw error;
      }
    },
    onSuccess: () => {
      toast({
        description: "Updated successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
      setIsPauseModalOpen(false);
      refetch();
      parentRefetch && parentRefetch();
    },
    onError: (error: any) => {
      toast({
        description: `Error Updating: ${
          error?.response?.data?.errors?.toString() ?? "Something went wrong"
        }`,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    },
  });

  const resumeMutation = useMutation<any, Error, any, unknown>({
    mutationFn: async (payload) => {
      try {
        const response = await resumeMessageCampaign({
          id: selectedCampaign,
          dealership_id: user?.dealership?.id,
        });
        return { data: response.data };
      } catch (error) {
        throw error;
      }
    },
    onSuccess: () => {
      toast({
        description: "Updated successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
      setIsPauseModalOpen(false);
      refetch();
      parentRefetch && parentRefetch();
    },
    onError: (error: any) => {
      toast({
        description: `Error Updating: ${
          error?.response?.data?.errors?.toString() ?? "Something went wrong"
        }`,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    },
  });

  useEffect(() => {
    if (shouldRefetch) {
      refetch();
      setShouldRefetch && setShouldRefetch(false);
    }
  }, [refetch, setShouldRefetch, shouldRefetch]);
  const defaultOption = [{ label: "Communication Log", value: "logs" }];

  const options: SelectOptionProps[] = useMemo(() => {
    let option = [
      //   { label: "Edit", value: "edit" },
      { label: "Communication Log", value: "logs" },
      //   { label: "Delete", value: "delete" },
    ];

    if (type !== "completed") {
      option.push({
        label: type === "pending" ? "Pause" : "Resume",
        value: type === "pending" ? "pause" : "resume",
      });
    }

    if (user?.role === "SimpSocial Owner") {
      option.push({ label: "Approve", value: "approve" });
    }

    return option;
  }, [type, user?.role]);

  const handleSelect = (option: SelectOptionProps, item: any) => {
    setSelectedCampaign(item.id);
    if (option.value === "logs") {
      return navigate(`/broadcast/communication/${item.id}`);
    }

    if (option.value === "pause") {
      return setIsPauseModalOpen(true);
    }

    if (option.value === "resume") {
      return resumeMutation.mutate({});
    }

    if (option.value === "edit") {
      return setIsOpenMsg(true);
    }

    if (option.value === "approve") {
      return statusMutation.mutate({
        status: "approve",
        campaignId: item.campaign_id,
        id: item.id,
        user_id: user.previousUserId,
      });
    }

    if (option.value === "reject") {
      return statusMutation.mutate({
        status: "reject",
        campaignId: item.campaign_id,
        id: item.id,
        user_id: user.previousUserId,
      });
    }
  };

  const filterOptions = (
    options: any,
    status: string,
    schedule_campaign_status: string
  ) => {
    if (status === "pending") {
      return [
        ...options.filter(
          (option: any) => !["resume", "pause"].includes(option.value)
        ),
        ...(user?.full_name === "SimpSocial Engage" && !schedule_campaign_status
          ? editOptions
          : []),
      ];
    }
    if (
      status === "completed" ||
      status === "rejected" ||
      status === "pending" ||
      type === "completed"
    ) {
      return options.filter(
        (option: any) => !["resume", "pause"].includes(option.value)
      );
    }

    return options;
  };

  const messageData = useMemo(() => {
    if (!data?.data?.messages_campaigns?.length) {
      return [];
    }

    return data?.data?.messages_campaigns.map((message: any) => {
      const transformedOptions = filterOptions(
        options.map((option) => {
          if (
            ["pending", "in_progress"].includes(message.message_status) &&
            option.value === "resume"
          ) {
            return {
              value: "pause",
              label: "Pause",
            };
          }
          return option;
        }),
        message.message_status,
        message.schedule_campaign_status
      );
      return {
        ...message,
        broadcast_time: message.broadcast_time
          ? moment(message.broadcast_time).format("MM-DD-YYYY hh:mm A")
          : "- -",
        message_status: humanize(message.message_status),
        failed_sms: message?.failed_sms || 0,
        engagement: "- -",
        engagement_rate: message?.engagement_rate + "%",
        throttling_rate:
          throttlingOptions[
            message?.throttling_rate as keyof typeof throttlingOptions
          ],
        completed_percentage: message?.completed_percentage + "%",
        message_recieved: message?.message_recieved || 0,
        completed_at: message.completed_at
          ? moment(message.completed_at).format("MM-DD-YYYY hh:mm A")
          : "- -",
        actions: transformedOptions?.length ? (
          <VStack p="0.5rem">
            <Dropdown
              options={transformedOptions}
              onSelect={(option) => handleSelect(option, message)}
            />
          </VStack>
        ) : (
          <></>
        ),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.data?.messages_campaigns, options, type]);

  const handlePause = ({
    scheduleDate,
    isIndefinitePeriod,
  }: {
    scheduleDate: string;
    isIndefinitePeriod: boolean;
  }) => {
    mutation.mutate({
      schedule_date: scheduleDate,
      indefinite_period: isIndefinitePeriod,
    });
  };

  const transformedHeaders = useMemo(() => {
    if (!type || type === "completed") {
      return [...tableHeader].sort((a, b) => a.id - b.id);
    }

    const filteredHeaders = tableHeader.filter(
      (value) => value.label !== "message_status"
    );

    if (type === "rejected") {
      return [
        ...filteredHeaders,
        { id: 8, value: "Rejection Reason", label: "notes" },
      ].sort((a, b) => a.id - b.id);
    }

    return filteredHeaders;
  }, [type]);

  return (
    <div>
      {(mutation.isLoading || statusMutation.isLoading) && <Loader />}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        padding="1rem"
      >
        <Text textStyle="caption" color="var(--grey-600)">
          Broadcast Found: {data?.data?.total_messages || 0}
        </Text>

        <Box>
          <DatePicker
            label="Select Date Range"
            shouldShowLabel={false}
            value={{
              startDate: dateRange.start_date as unknown as Date,
              endDate: dateRange.end_date as Date,
            }}
            onChange={(range: {
              startDate: Date;
              endDate: Date;
              clear?: boolean;
            }) => {
              setDateRange({
                start_date: range?.startDate as any,
                end_date: range?.endDate,
                selected: !range.clear,
              });
              setPage(1);
            }}
            w="100%"
            shouldShowClear
            isRange
          />
        </Box>
      </Box>
      <PaginatedTable
        header={transformedHeaders}
        data={messageData}
        itemsPerPage={perPage}
        maxPageNumbers={5}
        isPaginated={true}
        hasMultiBody={false}
        lastCenter={false}
        perPageChange={(value) => {
          setPerPage(Number(value?.label));
          setPage(1);
        }}
        currentPage={page}
        setCurrentPage={setPage}
        rowCount={data?.data?.total_messages || 0}
        isLoadingData={isLoading}
      />

      {isPauseModalOpen && (
        <PauseModal
          isOpen={isPauseModalOpen}
          handlePause={handlePause}
          onClose={() => setIsPauseModalOpen(false)}
        />
      )}

      {isOpenMsg && (
        <Drawer
          isOpen={isOpenMsg}
          onClose={onCloseMsg}
          title="Edit Bulk Message"
        >
          <CreateBulkMessageForm
            onCloseMsg={() => {
              onCloseMsg();
              setShouldRefetch && setShouldRefetch(true);
              refetch();
            }}
            isEdit
            id={selectedCampaign}
          />
        </Drawer>
      )}
    </div>
  );
}

export default BroadcastMessageListing;
